import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Ban, Clock, MessageSquare, PenLine, Camera, CalendarClock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";

const KIND = {
  approved: { verb: "hat freigegeben", icon: Check, cls: "text-emerald-400" },
  rejected: { verb: "hat gesperrt", icon: Ban, cls: "text-rose-400" },
  pending_approval: { verb: "hat zurück auf offen gesetzt", icon: Clock, cls: "text-amber-300" },
  changes_requested: { verb: "wünscht eine Änderung", icon: PenLine, cls: "text-orange-400" },
  comment: { verb: "hat kommentiert", icon: MessageSquare, cls: "text-sky-400" },
  shoot_confirmed: { verb: "hat den Drehtermin bestätigt", icon: Camera, cls: "text-emerald-400" },
  shoot_counter: { verb: "schlägt einen anderen Drehtermin vor", icon: Camera, cls: "text-amber-300" },
  moved: { verb: "hat einen Beitrag verschoben", icon: CalendarClock, cls: "text-sky-300" },
};

const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} Min.`;
  if (m < 1440) return `vor ${Math.round(m / 60)} Std.`;
  return new Date(iso).toLocaleDateString("de-DE");
};

export default function NotificationBell({ testId = "notification-bell" }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const known = useRef(null);
  const navigate = useNavigate();
  const { setClientId } = useClient();

  const load = useCallback(async () => {
    const r = await api.get("/notifications").catch(() => null);
    if (!r) return;
    const ids = new Set(r.data.items.map((n) => n.id));
    if (known.current) {
      r.data.items.filter((n) => !known.current.has(n.id) && !n.read).reverse().forEach((n) => {
        toast(`${n.client_name}: ${n.actor} ${KIND[n.kind]?.verb}`, { description: n.text ? `„${n.text}“ – ${n.post_title}` : n.post_title });
      });
    }
    known.current = ids;
    setData(r.data);
    document.title = r.data.unread ? `(${r.data.unread}) Content Management` : "Content Management";
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setClientId(n.client_id);
    setOpen(false);
    navigate(n.kind?.startsWith("shoot") ? "/" : n.kind === "moved" ? "/kalender" : "/freigaben");
    load();
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 grid place-items-center rounded-lg text-slate-300 hover:bg-white/5" data-testid={testId} title="Mitteilungen">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center" data-testid={`${testId}-count`}>{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="dark w-[92vw] max-w-sm p-0 bg-[#33373f] border-border text-foreground" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-semibold text-sm">Mitteilungen</p>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-teal-400 hover:underline" data-testid="notifications-read-all">Alle gelesen</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto scroll-thin">
          {data.items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">Noch keine Mitteilungen.</p>}
          {data.items.map((n) => {
            const k = KIND[n.kind] || KIND.comment;
            return (
              <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 hover:bg-white/[0.03] ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
                <k.icon className={`h-4 w-4 mt-0.5 shrink-0 ${k.cls}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug"><span className="font-semibold">{n.client_name}</span> {k.verb}</p>
                  <p className="text-xs text-slate-400 truncate">{n.post_title}</p>
                  {n.text && <p className="text-xs text-slate-300 mt-1 line-clamp-2">„{n.text}“</p>}
                  <p className="text-[10px] text-slate-500 mt-1">{ago(n.created_at)}</p>
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-teal-400 mt-1.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
